import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import type { SupportTicketDto } from "@finora/types";
import { Button, Skeleton, StatusBadge } from "@finora/ui";
import { apiFetch } from "../../lib/api";

const STATUS_LABELS: Record<string, string> = {
  OPEN: "Otvoren",
  IN_PROGRESS: "U obradi",
  RESOLVED: "Rešen",
  CLOSED: "Zatvoren",
};
const STATUS_TONE: Record<string, "success" | "error" | "neutral" | "warning"> = {
  OPEN: "warning",
  IN_PROGRESS: "neutral",
  RESOLVED: "success",
  CLOSED: "neutral",
};

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("sr-RS");
}

export function SupportTicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState("");

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["support-ticket", id],
    queryFn: () => apiFetch<SupportTicketDto>(`/support-tickets/${id}`),
    enabled: !!id,
  });

  const sendReply = useMutation({
    mutationFn: () =>
      apiFetch<SupportTicketDto>(`/support-tickets/${id}/messages`, {
        method: "POST",
        body: JSON.stringify({ message: reply }),
      }),
    onSuccess: () => {
      setReply("");
      queryClient.invalidateQueries({ queryKey: ["support-ticket", id] });
      queryClient.invalidateQueries({ queryKey: ["support-tickets"] });
    },
  });

  if (isLoading || !ticket) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const closed = ticket.status === "RESOLVED" || ticket.status === "CLOSED";

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/podrska")}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Nazad na tikete
      </button>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{ticket.subject}</h1>
          <p className="text-sm text-slate-500">Otvoren {formatDateTime(ticket.createdAt)}</p>
        </div>
        <StatusBadge
          label={STATUS_LABELS[ticket.status] ?? ticket.status}
          tone={STATUS_TONE[ticket.status] ?? "neutral"}
        />
      </div>

      <div className="space-y-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        {ticket.messages?.map((m) => (
          <div
            key={m.id}
            className={`rounded-lg p-3 text-sm ${
              m.authorType === "ADMIN" ? "bg-blue-50 text-blue-900" : "bg-slate-50 text-slate-800"
            }`}
          >
            <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
              <span className="font-medium">
                {m.authorType === "ADMIN" ? m.authorName ?? "HAFiskal podrška" : m.authorName ?? "Vi"}
              </span>
              <span>{formatDateTime(m.createdAt)}</span>
            </div>
            <p className="whitespace-pre-wrap">{m.message}</p>
          </div>
        ))}
      </div>

      {closed ? (
        <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm text-slate-500">
          Ovaj tiket je {ticket.status === "RESOLVED" ? "rešen" : "zatvoren"}. Ako dopišete poruku, tiket
          se automatski ponovo otvara.
        </p>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (reply.trim()) sendReply.mutate();
        }}
        className="space-y-2"
      >
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          placeholder="Napišite odgovor..."
          className="w-full rounded-lg border border-border px-3 py-2 text-sm"
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={sendReply.isPending || !reply.trim()}>
            {sendReply.isPending ? "Šaljem..." : "Pošalji"}
          </Button>
        </div>
      </form>
    </div>
  );
}
